/**
 * Duplicate-detection helpers for job creation & invoicing.
 *
 * Pure, side-effect-free functions operating on the rows returned by
 * GET /requestimatesVIN/:installerId/:vin. Each row carries:
 *   request_id, request_type, requestor_name, service_requested,
 *   current_state, location_id, locationName, created_date, due_date, assigned_to
 *
 * Matching is intentionally SERVICE-AGNOSTIC: any same-VIN job in the relevant
 * lifecycle bucket counts. The endpoint returns no master_service_id/category_id,
 * and the guards are block-with-confirm, so we err toward catching every dupe.
 *
 * State buckets (compared case-insensitively against current_state):
 *   TERMINAL      — dead rows, never a duplicate
 *   COMPLETED     — job already finished/invoiced/paid
 *   pre-completed — everything else (scheduled or in-progress somewhere)
 */

import moment from 'moment';
import { normalizeVin, isPlaceholderVin } from './assetType.js';

export { normalizeVin, isPlaceholderVin };

// ---------------------------------------------------------------------------
// Wire-shape normalization
//
// GET /requestimatesVIN/:installerId/:vin returns MIXED casing — the id column
// is `Request_id` (capital R) and the service is `Service_Requested`, while
// `current_state` / `locationName` / `created_date` / `requestor_name` are
// lowercase. The same endpoint is consumed by selectCustomerJob.jsx, which
// keys on `Request_id` — that is the authoritative spelling.
//
// Reading `request_id` therefore yielded undefined, which rendered "Request #"
// with no number and made buildOpenJobMessage drop every row. Normalize here,
// at the boundary, so nothing downstream needs to know the wire casing.
// ---------------------------------------------------------------------------

/** First non-null value among `keys`. */
const pick = (row, ...keys) => {
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null) return row[k];
  }
  return null;
};

/** Map one wire row onto the canonical snake_case shape used by this module. */
export function normalizeVinRow(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    request_id: pick(row, 'request_id', 'Request_id', 'requestId', 'id'),
    request_type: pick(row, 'request_type', 'Request_type', 'requestType'),
    requestor_name: pick(row, 'requestor_name', 'Requestor_name', 'requestorName'),
    service_requested: pick(row, 'service_requested', 'Service_Requested', 'serviceRequested'),
    current_state: pick(row, 'current_state', 'Current_state', 'currentState'),
    location_id: pick(row, 'location_id', 'Location_id', 'locationId'),
    locationName: pick(row, 'locationName', 'location_name', 'LocationName'),
    created_date: pick(row, 'created_date', 'Created_date', 'createdDate'),
  };
}

/** Normalize a wire row array. Non-arrays collapse to []. */
export function normalizeVinRows(rows) {
  return Array.isArray(rows) ? rows.map(normalizeVinRow) : [];
}

export const TERMINAL_STATES = ['cancelled', 'deleted', 'void'];
export const COMPLETED_STATES = ['finished', 'collected', 'reviewed', 'paid'];
export const DRAFT_STATES = ['draft'];

/**
 * MIRROR of the server's `DUP_GUARD_INACTIVE_STATES`
 * (JobviousServer requestimateController.js:86). Everything NOT in this list
 * makes `POST /requestimates/` return 409 without `allowDuplicate`.
 *
 * This deliberately does NOT equal our own lifecycle buckets above: the server
 * still blocks on `finished` and `paid`, which we classify as completed, plus
 * `expired` and `LOSS`. Guard 2 must key off THIS list, not our buckets —
 * otherwise a state the server blocks but we consider harmless produces a bare
 * 409 with no "Create anyway" path, which is exactly the dead end users hit on
 * a finished-but-not-collected job.
 *
 * INVARIANT: the client must warn wherever the server would block. If the
 * server's list changes, change this one in the same commit.
 */
export const SERVER_DUP_INACTIVE_STATES = [
  'cancelled', 'deleted', 'void', 'collected', 'reviewed', 'draft',
];

/** Would the server's DUP-GUARD count this row as an active conflict? */
export function blocksCreation(state) {
  return !SERVER_DUP_INACTIVE_STATES.includes(String(state || '').trim().toLowerCase());
}

function stateOf(row) {
  return String((row && row.current_state) || '').trim().toLowerCase();
}

export function isTerminalState(state) {
  return TERMINAL_STATES.includes(String(state || '').trim().toLowerCase());
}

export function isCompletedState(state) {
  return COMPLETED_STATES.includes(String(state || '').trim().toLowerCase());
}

/** Parked, not-yet-submitted request — resumable, never a blocking duplicate. */
export function isDraftState(state) {
  return DRAFT_STATES.includes(String(state || '').trim().toLowerCase());
}

/**
 * Not terminal, not completed, and not a draft → the vehicle is scheduled or in
 * a shop. Drafts are excluded: they're parked work the user can resume, not an
 * open job that should block re-creation.
 */
export function isPreCompletedState(state) {
  return !isTerminalState(state) && !isCompletedState(state) && !isDraftState(state);
}

/**
 * Rows for this VIN that are pre-completed (open/in-flight) by OUR lifecycle
 * buckets. Retained as the semantic predicate; Guard 2 uses
 * `findCreateBlockingForVin` instead, which mirrors the server.
 */
export function findPreCompletedForVin(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((row) => isPreCompletedState(stateOf(row)));
}

/**
 * Guard 2 (creation): rows the SERVER would treat as an active conflict.
 * Service-agnostic. Use this — not `findPreCompletedForVin` — anywhere the
 * result decides whether to show the "Create anyway" confirm, so the confirm
 * appears in every case the server would reject.
 */
export function findCreateBlockingForVin(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((row) => blocksCreation(stateOf(row)));
}

/**
 * Parked drafts for this VIN. These are not blocking duplicates — the UI can
 * offer to resume them instead of starting a fresh request. Service-agnostic.
 */
export function findResumableDrafts(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.filter((row) => isDraftState(stateOf(row)));
}

/**
 * Guard 3 (invoice): OTHER rows for this VIN (different request_id) that are
 * already completed/invoiced. Service-agnostic.
 */
export function findCompletedOtherForVin(rows, excludeRequestId) {
  if (!Array.isArray(rows)) return [];
  // request_id may arrive as number or string — compare loosely.
  const exclude = excludeRequestId == null ? null : String(excludeRequestId);
  return rows.filter(
    (row) =>
      isCompletedState(stateOf(row)) &&
      (exclude == null || String(row.request_id) !== exclude)
  );
}

// ---------------------------------------------------------------------------
// 409 (duplicate open job) messaging — used by the create forms when the
// server rejects POST /requestimates/ with a conflict.
// ---------------------------------------------------------------------------

/**
 * Pull existing-job rows out of a 409 response body. Tolerant of shape:
 * accepts { requests: [...] } / { existingRequests: [...] } / { matches: [...] } /
 * { existing: {...} } / { duplicates: [...] } / a bare array / a bare row object.
 *
 * `existingRequests` is the server's original (pre-2026-08-05) envelope and is
 * still sent for back-compat; `requests` is the current one. Rows carry both
 * snake_case and camelCase spellings — normalization below collapses them.
 */
export function extractRowsFrom409(data) {
  if (!data) return [];
  const candidate =
    data.requests || data.existingRequests || data.matches || data.existing ||
    data.duplicates || data;
  if (Array.isArray(candidate)) return normalizeVinRows(candidate);
  if (candidate && (candidate.request_id || candidate.Request_id || candidate.requestId)) {
    return [normalizeVinRow(candidate)];
  }
  return [];
}

const rowField = (row, snake, camel) => (row && (row[snake] != null ? row[snake] : row[camel])) || null;

const titleCase = (s) => {
  const v = String(s || '').trim();
  return v ? v.charAt(0).toUpperCase() + v.slice(1) : '';
};

/**
 * One summary line for an existing open job:
 *   #{requestId} — {serviceRequested} · {currentState} · started {createdDate} · {locationName}
 * Missing fields are dropped, not rendered as blanks.
 */
export function formatOpenJobLine(row) {
  const requestId = rowField(row, 'request_id', 'requestId');
  const service = rowField(row, 'service_requested', 'serviceRequested');
  const state = titleCase(rowField(row, 'current_state', 'currentState'));
  const created = rowField(row, 'created_date', 'createdDate');
  const location = rowField(row, 'locationName', 'location_name');

  const m = created ? moment(created) : null;
  const createdLabel = m && m.isValid() ? m.format('MMM D, YYYY') : null;

  const parts = [
    service,
    state,
    createdLabel ? `started ${createdLabel}` : null,
    location,
  ].filter(Boolean);

  return `#${requestId}${parts.length ? ' — ' + parts.join(' · ') : ''}`;
}

/**
 * Full user-facing message for a create-time 409.
 *   The {year} {make} {model} already has an open job with you:
 *
 *   #{requestId} — {serviceRequested} · {currentState} · started {createdDate} · {locationName}
 * Multiple open jobs render one line each. With no row data (server body
 * didn't include it and the fallback lookup failed) a generic line is used.
 */
export function buildOpenJobMessage(vehicle, rows) {
  const desc = [vehicle && vehicle.year, vehicle && vehicle.make, vehicle && vehicle.model]
    .filter(Boolean).join(' ');
  const intro = `${desc ? `The ${desc}` : 'This vehicle'} already has an open job with you:`;

  const list = (Array.isArray(rows) ? rows : []).filter((r) => rowField(r, 'request_id', 'requestId'));
  if (!list.length) {
    return `${desc ? `The ${desc}` : 'This vehicle'} already has an open job with you. Check the open jobs list before creating a new request.`;
  }
  return `${intro}\n\n${list.map(formatOpenJobLine).join('\n')}`;
}
